import { useState, useCallback, useMemo } from 'react';
import type { DataGridColumn } from '../DataGrid.types';

export type PinPosition = 'left' | 'right' | null;

export interface UseColumnPinningOptions<T extends Record<string, unknown>> {
  columns: DataGridColumn<T>[];
  onColumnPin?: (columnId: string, position: PinPosition) => void;
}

export interface UseColumnPinningReturn<T extends Record<string, unknown>> {
  pinnedColumns: {
    left: DataGridColumn<T>[];
    center: DataGridColumn<T>[];
    right: DataGridColumn<T>[];
  };
  pinState: Record<string, PinPosition>;
  pinColumn: (columnId: string, position: PinPosition) => void;
  unpinColumn: (columnId: string) => void;
  isPinned: (columnId: string) => PinPosition;
  getPinnedWidth: (position: 'left' | 'right') => number;
}

export function useColumnPinning<T extends Record<string, unknown>>({
  columns,
  onColumnPin
}: UseColumnPinningOptions<T>): UseColumnPinningReturn<T> {
  const [pinState, setPinState] = useState<Record<string, PinPosition>>({});

  const pinColumn = useCallback((columnId: string, position: PinPosition) => {
    setPinState(prev => ({
      ...prev,
      [columnId]: position
    }));
    onColumnPin?.(columnId, position);
  }, [onColumnPin]);

  const unpinColumn = useCallback((columnId: string) => {
    setPinState(prev => {
      const newState = { ...prev };
      delete newState[columnId];
      return newState;
    });
    onColumnPin?.(columnId, null);
  }, [onColumnPin]);

  const isPinned = useCallback((columnId: string): PinPosition => {
    return pinState[columnId] || null;
  }, [pinState]);

  const pinnedColumns = useMemo(() => {
    const left: DataGridColumn<T>[] = [];
    const center: DataGridColumn<T>[] = [];
    const right: DataGridColumn<T>[] = [];

    columns.forEach(column => {
      const pinPosition = pinState[column.id];
      if (pinPosition === 'left') {
        left.push(column);
      } else if (pinPosition === 'right') {
        right.push(column);
      } else {
        center.push(column);
      }
    });

    return { left, center, right };
  }, [columns, pinState]);

  const getPinnedWidth = useCallback((position: 'left' | 'right'): number => {
    const cols = position === 'left' ? pinnedColumns.left : pinnedColumns.right;
    return cols.reduce((total, col) => total + (col.width || 150), 0);
  }, [pinnedColumns]);

  return {
    pinnedColumns,
    pinState,
    pinColumn,
    unpinColumn,
    isPinned,
    getPinnedWidth
  };
}
